/*************************************************************
 * Fixed OverviewTab.jsx - Properly integrated with AlertService
 * Refactored project fetching to use reportingApi methods.
 *************************************************************/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppContext } from '../context/AppContext.jsx';
import { api } from '../api/reportingApi.js';
import { LoadingSpinner } from '../components/ui/LoadingSpinner.jsx';
import { ErrorBoundary } from '../components/ui/ErrorBoundary.jsx';
import { TrendChart } from '../components/charts/TrendChart.jsx';
import { useAutoRefresh } from '../hooks/useAutoRefresh.js';
import ProjectDetailsPage from '../components/project_details.jsx'; 
import { AlertService } from '../services/AlertService.js';

const ProjectDetailsModal = ({ project, isOpen, onClose }) => {
    if (!isOpen || !project) return null;

    return (
        <div className={`modal fade ${isOpen ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-fullscreen-lg-down">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className="fas fa-project-diagram me-2"></i>
                            Detailed Analysis: {project.project_name}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <ProjectDetailsPage 
                            projectName={project.project_name} 
                            onNavigateBack={onClose}
                        />
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Close
                        </button>                    
                    </div>
                </div>
            </div>
        </div>
    );
};

export const OverviewTab = () => {
    const { 
        state, 
        setLoading, 
        setError, 
        addNotification, 
        setCurrentTab,
        updateFilters,
        withManualImagesPref
    } = useAppContext();
 
    // Dashboard data state
    const [dashboardData, setDashboardData] = useState(null);
    const [trendData, setTrendData] = useState(null);
    const [recentProjects, setRecentProjects] = useState([]);
    const [isLoadingProjects, setIsLoadingProjects] = useState(true);
    const [selectedProject, setSelectedProject] = useState(null);
    const [showProjectDetails, setShowProjectDetails] = useState(false);

    // Alert state (managed by AlertService)
    const [alerts, setAlerts] = useState([]);
    const [alertsSummary, setAlertsSummary] = useState({});
    const [notifications, setNotifications] = useState([]);
    
    // AlertService instance
    const [alertService] = useState(() => AlertService.createForOverview());
    const [alertPollingInterval, setAlertPollingInterval] = useState(null);    
    
    // Get configuration values from context
    const {
        autoRefresh,
        refreshInterval,
        show_confidence_scores,
        itemsPerPage,
        default_date_range,
        includeManualImages
    } = state.preferences;

    // Initialize filters with configuration preferences on mount AND when config changes
    useEffect(() => {
        console.log('[OverviewTab] Configuration effect triggered:', {
            default_date_range,
            currentDateRange: state.filters.dateRange,
            configLoaded: state.configuration.isLoaded
        });

        // Only update if configuration is loaded and we don't have a date range set
        if (state.configuration.isLoaded && default_date_range) {
            // Check if we need to update the date range
            // if (!state.filters.dateRange || state.filters.dateRange !== default_date_range) {
            if (!state.filters.dateRange) {
                console.log(`[OverviewTab] Updating date range from '${state.filters.dateRange}' to '${default_date_range}'`);
                updateFilters({ 
                    ...state.filters, 
                    dateRange: default_date_range 
                });
            }
        }
    }, [
        state.configuration.isLoaded, 
        default_date_range, 
        state.filters.dateRange, 
        updateFilters//, 
        // state.filters
    ]);

    // Setup AlertService polling
    useEffect(() => {
        console.log('[OverviewTab] Setting up AlertService polling...');
        
        // Start polling for alerts using AlertService
        const pollInterval = alertService.startBackendAlertPolling(async (data) => {
            console.log('[OverviewTab] AlertService update:', data);
            
            try {
                // Update state with formatted alerts
                const formattedAlerts = data.alerts.map(alert => alertService.formatAlertForUI(alert));
                setAlerts(formattedAlerts);
                setAlertsSummary(data.summary || {});
                setNotifications(data.notifications || []);
                
                // Show notification for new critical alerts
                const criticalAlerts = formattedAlerts.filter(a => 
                    a.severity === 'critical' && a.status === 'active'
                );
                
                if (criticalAlerts.length > 0) {
                    criticalAlerts.forEach(alert => {
                        AlertService.sendInAppNotification({
                            rule: { name: alert.name, type: alert.type },
                            message: alert.message
                        });
                    });
                }
            } catch (error) {
                console.error('[OverviewTab] Error processing alert update:', error);
            }
            
        }, 30000); // Poll every 30 seconds

        setAlertPollingInterval(pollInterval);

        // Cleanup on unmount
        return () => {
            if (alertService) {
                alertService.stopBackendAlertPolling();
            }
        };
    }, [alertService]);

    // Enhanced alert action handlers using AlertService
    const handleAcknowledgeAlert = useCallback(async (alertId) => {
        try {
            console.log('[OverviewTab] Acknowledging alert via AlertService:', alertId);
            
            const success = await alertService.acknowledgeAlert(alertId);
            
            if (success) {
                addNotification({
                    type: 'success',
                    message: 'Alert acknowledged successfully',
                    duration: 3000
                });
                
                // Update local state immediately for better UX
                setAlerts(prev => prev.map(alert => 
                    alert.id === alertId 
                        ? { ...alert, status: 'acknowledged', canAcknowledge: false }
                        : alert
                ));
            } else {
                throw new Error('Failed to acknowledge alert');
            }
        } catch (error) {
            console.error('[OverviewTab] Error acknowledging alert:', error);
            addNotification({
                type: 'danger',
                message: 'Failed to acknowledge alert: ' + error.message,
                duration: 5000
            });
        }
    }, [alertService, addNotification]);

    const handleResolveAlert = useCallback(async (alertId) => {
        try {
            console.log('[OverviewTab] Resolving alert via AlertService:', alertId);
            
            const success = await alertService.resolveAlert(alertId);
            
            if (success) {
                addNotification({
                    type: 'success',
                    message: 'Alert resolved successfully',
                    duration: 3000
                });
                
                // Remove from local state immediately for better UX
                setAlerts(prev => prev.filter(alert => alert.id !== alertId));
            } else {
                throw new Error('Failed to resolve alert');
            }
        } catch (error) {
            console.error('[OverviewTab] Error resolving alert:', error);
            addNotification({
                type: 'danger',
                message: 'Failed to resolve alert: ' + error.message,
                duration: 5000
            });
        }
    }, [alertService, addNotification]);

    // Force refresh alerts
    const handleRefreshAlerts = useCallback(async () => {
        try {
            console.log('[OverviewTab] Force refreshing alerts...');
            
            // Stop current polling
            if (alertService) {
                alertService.stopBackendAlertPolling();
            }
            
            // Start new polling session
            const newInterval = alertService.startBackendAlertPolling((data) => {
                const formattedAlerts = data.alerts.map(alert => alertService.formatAlertForUI(alert));
                setAlerts(formattedAlerts);
                setAlertsSummary(data.summary || {});
                setNotifications(data.notifications || []);
            }, 30000);
            
            setAlertPollingInterval(newInterval);
            
            addNotification({
                type: 'info',
                message: 'Alerts refreshed',
                duration: 2000
            });
        } catch (error) {
            console.error('[OverviewTab] Error refreshing alerts:', error);
            addNotification({
                type: 'warning',
                message: 'Failed to refresh alerts: ' + error.message,
                duration: 3000
            });
        }
    }, [alertService, addNotification]);

    // Test alert functionality (for development/debugging)
    const handleTestAlert = useCallback(async () => {
        try {
            console.log('[OverviewTab] Testing alert system...');
            
            const result = await alertService.testNotifications(state.preferences);
            
            if (result.success) {
                addNotification({
                    type: 'success',
                    message: 'Alert system test completed successfully',
                    duration: 3000
                });
            } else {
                addNotification({
                    type: 'warning',
                    message: `Alert test partially failed: ${result.error || 'Unknown error'}`,
                    duration: 5000
                });
            }
        } catch (error) {
            console.error('[OverviewTab] Error testing alerts:', error);
            addNotification({
                type: 'danger',
                message: 'Failed to test alert system: ' + error.message,
                duration: 5000
            });
        }
    }, [alertService, addNotification, state.preferences]);

    // Load recent projects with configuration preferences
    const loadRecentProjects = useCallback(async () => {
        try {
            setIsLoadingProjects(true);
            console.log('[OverviewTab] Loading recent projects...');
            
            // Use itemsPerPage preference for limiting results
            const limit = Math.min(itemsPerPage || 20, 100); // Cap at 100 for performance
            
            // Apply manual images preference to the request
            const configuredFilters = withManualImagesPref({});
            
            console.log('[OverviewTab] Loading projects with filters:', configuredFilters);
            console.log('[OverviewTab] Projects limit:', limit);

            // OLD: Direct fetch for basic list
            // const basicResponse = await fetch(`http://localhost:1019/api/v1/projects?page=1&limit=${limit}`, {
            //     method: 'GET',
            //     headers: {
            //         'Content-Type': 'application/json',
            //         ...(localStorage.getItem('access_token') && { 
            //             'Authorization': `Bearer ${localStorage.getItem('access_token')}` 
            //         })
            //     },
            //     credentials: 'include'
            // });
            // const basicData = await basicResponse.json();

            // NEW: Use reportingApi.getPaginatedProjects
            // This returns an object like { data: [projects], total: X }

            // const basicData = await api.getPaginatedProjects(1, limit); 
            // const basicProjects = basicData?.data || [];
            const basicProjects = await api.getPaginatedProjects(1, limit); 


            if (!Array.isArray(basicProjects) || basicProjects.length === 0) {
                return [];
            }

            const projectNames = basicProjects.map(p => p.name);
            console.log('[OverviewTab] Fetching details for projects:', projectNames);

            // The detailsPayload filters are now passed directly to the API method
            // const detailsPayload = {
            //     project_names: projectNames,
            //     ...configuredFilters // Include manual images preference
            // };

            // OLD: Direct fetch for details
            // const detailsResponse = await fetch('http://localhost:1019/api/v1/reports/projects/details', {
            //     method: 'POST',
            //     headers: {
            //         'Content-Type': 'application/json',
            //         ...(localStorage.getItem('access_token') && { 
            //             'Authorization': `Bearer ${localStorage.getItem('access_token')}` 
            //         })
            //     },
            //     credentials: 'include',
            //     body: JSON.stringify(detailsPayload)
            // });
            // const detailsData = await detailsResponse.json();

            // NEW: Use reportingApi.getProjectDetails (updated to accept filters)
            // This call returns the map of project data directly.
            const projectDetailsMap = await api.getProjectDetails(projectNames, configuredFilters);


            // Transform the data
            const transformedProjects = [];
            
            // Original code used detailsData.success/detailsData.data, but now projectDetailsMap 
            // is the map directly, so we check if it's truthy.
            if (projectDetailsMap) {
                Object.entries(projectDetailsMap).forEach(([projectName, projectData]) => {
                    transformedProjects.push({
                        project_name: projectName,
                        total_inspections: projectData.total_inspections || 0,
                        defect_count: projectData.defect_count || 0,
                        normal_count: projectData.normal_count || 0,
                        defect_rate: projectData.defect_rate || 0,
                        last_inspection: projectData.last_inspection,
                        latest_image: projectData.latest_image || 'http://localhost:5000/assets/no-training-image.png',
                        confidence: projectData.avg_confidence || projectData.confidence
                    });
                });
            }
            
            // Sort by last_inspection and take the most recent
            const sortedProjects = transformedProjects
                .filter(p => p.last_inspection)
                .sort((a, b) => {
                    const dateA = new Date(a.last_inspection).getTime();
                    const dateB = new Date(b.last_inspection).getTime();
                    return dateB - dateA;
                })
                .slice(0, 6); // Always show max 6 for UI layout
            
            return sortedProjects;
            
        } catch (error) {
            console.error('[OverviewTab] Error loading recent projects:', error);
            // Handle API errors gracefully
            addNotification({
                type: 'danger',
                message: `Failed to load recent projects: ${error.message}`
            });
            return [];
        } finally {
            setIsLoadingProjects(false);
        }
    }, [itemsPerPage, withManualImagesPref, addNotification]);

    // Main data loading function with configuration integration
    const loadDashboard = useCallback(async () => {
        if (state.loading) {
            console.log("[OverviewTab] Load already in progress, skipping.");
            return;
        }

        console.log('[OverviewTab] loadDashboard called with filters:', state.filters);
        
        try {
            setLoading(true);
            setError(null);

            // Apply configuration preferences to filters
            const configuredFilters = withManualImagesPref(state.filters);
            
            console.log('[OverviewTab] Loading with configured filters:', configuredFilters);
            console.log('[OverviewTab] Using items per page:', itemsPerPage);
            console.log('[OverviewTab] Include manual images:', includeManualImages);

            // Load dashboard data in parallel
            const [dashboard, trend, recentProjectsData] = await Promise.all([
                api.getDashboardData(configuredFilters),
                api.getDashboardTrend(configuredFilters),
                loadRecentProjects()
            ]);

            setDashboardData(dashboard);
            setTrendData(trend);
            setRecentProjects(recentProjectsData);

            addNotification({
                type: 'success',
                message: 'Dashboard data loaded successfully',
                duration: 3000
            });
            
        } catch (error) {
            console.error('[OverviewTab] Error loading:', error);
            setError(error);
            addNotification({
                type: 'danger',
                message: 'Failed to load dashboard data: ' + error.message
            });
        } finally {
            setLoading(false);
        }
    }, [
        state.loading,
        state.filters, 
        itemsPerPage, 
        includeManualImages,
        withManualImagesPref,
        setLoading,
        setError,
        addNotification,
        loadRecentProjects
    ]);

    // Set up auto-refresh with user-configured interval
    const { isActive: autoRefreshActive, forceRefresh } = useAutoRefresh(
        loadDashboard, 
        [state.filters, includeManualImages], // Include manual images preference in dependencies
        {
            minInterval: 10,
            maxInterval: 300,
            logRefresh: true
        }
    );

    // FIXED: Only reload data when filters change, not on every loadDashboard change
    useEffect(() => {
        console.log('[OverviewTab] Filters changed, reloading data:', state.filters);
        loadDashboard();
    }, [state.filters, includeManualImages, itemsPerPage]); // Removed loadDashboard from deps

    // Debug effect to log configuration changes
    useEffect(() => {
        console.log('[OverviewTab] Configuration changed:', {
            autoRefresh,
            refreshInterval,
            show_confidence_scores,
            includeManualImages,
            default_date_range,
            itemsPerPage,
            alertsCount: alerts.length,
            activeAlerts: alerts.filter(a => a.status === 'active').length
        });
    }, [autoRefresh, refreshInterval, show_confidence_scores, includeManualImages, 
        default_date_range, itemsPerPage, alerts]);

    // Enhanced alert display component
    const renderEnhancedAlerts = () => {
        if (alerts.length === 0) return null;

        return (
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0 border-start border-warning border-4">
                        <div className="card-header bg-light">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <i className="fas fa-exclamation-triangle text-warning me-2"></i>
                                    Active Alerts ({alerts.length})
                                    {alertsSummary.has_critical_alerts && (
                                        <span className="badge badge-danger ms-2">CRITICAL</span>
                                    )}
                                </h5>
                                <div className="btn-group">
                                    <button 
                                        className="btn btn-outline-warning btn-sm"
                                        onClick={handleRefreshAlerts}
                                        disabled={state.loading}
                                    >
                                        <i className="fas fa-sync-alt me-1"></i>
                                        Refresh Alerts
                                    </button>
                                    <button 
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => handleNavigateToTab('configurations')}
                                    >
                                        <i className="fas fa-cog me-1"></i>
                                        Manage Rules
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                {alerts.slice(0, 6).map((alert, idx) => (
                                    <div key={`${alert.id}-${idx}`} className="col-md-6 mb-3">
                                        <div className={`alert ${alertService.getSeverityClass(alert.severity)} mb-0`}>
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <div className="d-flex align-items-center mb-1">
                                                        <strong>{alert.name}</strong>
                                                        <span className={`badge ${alertService.getSeverityBadgeClass(alert.severity)} ms-2`}>
                                                            {alert.severity.toUpperCase()}
                                                        </span>
                                                        {alert.status === 'acknowledged' && (
                                                            <span className="badge badge-info ms-1">ACK</span>
                                                        )}
                                                    </div>
                                                    <p className="mb-1 small">
                                                        {alert.message}
                                                    </p>
                                                    <small className="text-muted">
                                                        {alert.timeAgo}
                                                        {alert.stationId && ` • Station: ${alert.stationId}`}
                                                        {alert.projectName && ` • Project: ${alert.projectName}`}
                                                    </small>
                                                </div>
                                                <div className="btn-group btn-group-sm">
                                                    {alert.canAcknowledge && (
                                                        <button
                                                            className="btn btn-outline-warning btn-sm"
                                                            onClick={() => handleAcknowledgeAlert(alert.id)}
                                                            title="Acknowledge alert"
                                                            disabled={state.loading}
                                                        >
                                                            <i className="fas fa-check"></i>
                                                        </button>
                                                    )}
                                                    {alert.canResolve && (
                                                        <button
                                                            className="btn btn-outline-success btn-sm"
                                                            onClick={() => handleResolveAlert(alert.id)}
                                                            title="Resolve alert"
                                                            disabled={state.loading}
                                                        >
                                                            <i className="fas fa-check-double"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            {alerts.length > 6 && (
                                <div className="text-center mt-3">
                                    <button 
                                        className="btn btn-outline-primary"
                                        onClick={() => handleNavigateToTab('configurations')}
                                    >
                                        View All {alerts.length} Alerts
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Debug info for alerts (only in development)
    const renderAlertDebugInfo = () => {
        if (process.env.NODE_ENV !== 'development') return null;
        
        return (
            <div className="row mb-2">
                <div className="col-12">
                    <div className="alert alert-info">
                        <small>
                            <strong>Alert Debug:</strong> 
                            {alerts.length} active alerts, 
                            {notifications.length} notifications, 
                            Polling: {alertPollingInterval ? 'Active' : 'Inactive'}
                            <button 
                                className="btn btn-sm btn-outline-primary ms-2"
                                onClick={handleTestAlert}
                            >
                                Test Alerts
                            </button>
                        </small>
                    </div>
                </div>
            </div>
        );
    };

    // Utility functions
    const formatNumber = (num) => {
        if (num === null || num === undefined) return '0';
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num.toString();
    };

    const formatPercentage = (num, decimals = 1) => {
        return `${(num || 0).toFixed(decimals)}%`;
    };

    // Navigation handlers
    const handleNavigateToTab = (tabId) => {
        setCurrentTab(tabId);
    };

    // Handle project details modal
    const handleViewPartDetails = (project) => {
        console.log('[OverviewTab] Opening project details for:', project.project_name);
        setSelectedProject(project);
        setShowProjectDetails(true);
    };

    const handleCloseProjectDetails = () => {
        setShowProjectDetails(false);
        setSelectedProject(null);
    };

    if (state.loading && !dashboardData) {
        return (
            <LoadingSpinner
                message="Loading dashboard data..."
                size="large"
            />
        );
    }

    return (
        <ErrorBoundary error={state.error} onRetry={loadDashboard}>
            <div className="dashboard-content fade-in">
                {/* Debug info (development only) */}
                {renderAlertDebugInfo()}
                
                {/* Auto-refresh and manual refresh controls */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-end align-items-center">
                            <div className="d-flex align-items-center">
                                {/* Auto-refresh status indicator */}
                                {autoRefreshActive && (
                                    <div className="auto-refresh-indicator me-3">
                                        <i className="fas fa-sync-alt fa-spin text-success me-1"></i>
                                        <small className="text-muted">
                                            Auto-refresh: {refreshInterval}s
                                        </small>
                                    </div>
                                )}
                                
                                {/* Alert polling indicator */}
                                {alertPollingInterval && (
                                    <div className="alert-polling-indicator me-3">
                                        <i className="fas fa-bell text-warning me-1"></i>
                                        <small className="text-muted">
                                            Alerts: Live
                                        </small>
                                    </div>
                                )}
                                
                                {/* Alert summary in status bar */}
                                {alerts.length > 0 && (
                                    <div className="alert-summary-indicator me-3">
                                        <span className={`badge ${alerts.some(a => a.severity === 'critical') ? 'badge-danger' : 'badge-warning'} me-1`}>
                                            {alerts.length}
                                        </span>
                                        <small className="text-muted">
                                            Active Alert{alerts.length !== 1 ? 's' : ''}
                                        </small>
                                    </div>
                                )}
                                
                                <button 
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={forceRefresh}
                                    disabled={state.loading}
                                >
                                    <i className={`fas fa-sync-alt me-2 ${state.loading ? 'fa-spin' : ''}`}></i>
                                    Refresh
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Enhanced Backend-Driven Active Alerts Section */}
                {renderEnhancedAlerts()}

                {/* Key Metrics Row - with conditional confidence display */}
                {dashboardData && (
                    <div className="row mb-4">
                        <div className="col-md-3">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <p className="text-muted mb-1 small">Total Inspections</p>
                                            <h3 className="text-primary mb-0 fw-bold">
                                                {formatNumber(dashboardData.total_inspections)}
                                            </h3>
                                        </div>
                                        <div className="rounded-circle bg-primary bg-opacity-10 p-3">
                                            <i className="fas fa-microscope text-primary"></i>
                                        </div>
                                    </div>
                                    {dashboardData.trend_comparison?.inspection_count_change !== undefined && (
                                        <div className="d-flex align-items-center">
                                            <span className={`badge bg-${
                                                dashboardData.trend_comparison.inspection_count_change > 0 ? 'success' :
                                                dashboardData.trend_comparison.inspection_count_change < 0 ? 'danger' : 'secondary'
                                            } me-2`}>
                                                <i className={`fas fa-arrow-${
                                                    dashboardData.trend_comparison.inspection_count_change > 0 ? 'up' :
                                                    dashboardData.trend_comparison.inspection_count_change < 0 ? 'down' : 'right'
                                                }`}></i>
                                                {Math.abs(dashboardData.trend_comparison.inspection_count_change)}%
                                            </span>
                                            <small className="text-muted">vs last period</small>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <p className="text-muted mb-1 small">Defect Rate</p>
                                            <h3 className="text-danger mb-0 fw-bold">
                                                {formatPercentage(dashboardData.defect_rate)}
                                            </h3>
                                        </div>
                                        <div className="rounded-circle bg-danger bg-opacity-10 p-3">
                                            <i className="fas fa-exclamation-triangle text-danger"></i>
                                        </div>
                                    </div>
                                    {dashboardData.trend_comparison?.defect_rate_change !== undefined && (
                                        <div className="d-flex align-items-center">
                                            <span className={`badge bg-${
                                                dashboardData.trend_comparison.defect_rate_change > 0 ? 'danger' :
                                                dashboardData.trend_comparison.defect_rate_change < 0 ? 'success' : 'secondary'
                                            } me-2`}>
                                                <i className={`fas fa-arrow-${
                                                    dashboardData.trend_comparison.defect_rate_change > 0 ? 'up' :
                                                    dashboardData.trend_comparison.defect_rate_change < 0 ? 'down' : 'right'
                                                }`}></i>
                                                {Math.abs(dashboardData.trend_comparison.defect_rate_change)}%
                                            </span>
                                            <small className="text-muted">vs last period</small>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        {/* Conditionally show confidence score based on user preferences */}
                        {show_confidence_scores && (
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm h-100">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div>
                                                <p className="text-muted mb-1 small">Model Confidence</p>
                                                <h3 className="text-success mb-0 fw-bold">
                                                    {formatPercentage((dashboardData.model_performance?.avg_confidence || 0) * 100)}
                                                </h3>
                                            </div>
                                            <div className="rounded-circle bg-success bg-opacity-10 p-3">
                                                <i className="fas fa-brain text-success"></i>
                                            </div>
                                        </div>
                                        {dashboardData.trend_comparison?.confidence_change !== undefined && (
                                            <div className="d-flex align-items-center">
                                                <span className={`badge bg-${
                                                    dashboardData.trend_comparison.confidence_change > 0 ? 'success' :
                                                    dashboardData.trend_comparison.confidence_change < 0 ? 'danger' : 'secondary'
                                                } me-2`}>
                                                    <i className={`fas fa-arrow-${
                                                        dashboardData.trend_comparison.confidence_change > 0 ? 'up' :
                                                        dashboardData.trend_comparison.confidence_change < 0 ? 'down' : 'right'
                                                    }`}></i>
                                                    {Math.abs(dashboardData.trend_comparison.confidence_change)}%
                                                </span>
                                                <small className="text-muted">vs last period</small>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <div className={`col-md-${show_confidence_scores ? '3' : '6'}`}>
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <p className="text-muted mb-1 small">Active Sessions</p>
                                            <h3 className="text-info mb-0 fw-bold">
                                                {dashboardData.active_sessions?.toString() || '0'}
                                            </h3>
                                        </div>
                                        <div className="rounded-circle bg-info bg-opacity-10 p-3">
                                            <i className="fas fa-play-circle text-info"></i>
                                        </div>
                                    </div>
                                    <small className="text-muted">Currently running</small>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Quick Actions & Recent Parts Row */}
                <div className="row mb-4">
                    {/* Quick Actions */}
                    <div className="col-lg-4 mb-4">
                        <div className="card shadow-sm h-100">
                            <div className="card-header bg-light">
                                <h5 className="mb-0">
                                    <i className="fas fa-rocket text-primary me-2"></i>
                                    Quick Actions
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-6">
                                        <div className="card h-100 shadow-sm cursor-pointer quick-action-card" 
                                             onClick={() => handleNavigateToTab('projects')}
                                             style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}>
                                            <div className="card-body text-center">
                                                <i className="fas fa-project-diagram fa-2x text-primary mb-3"></i>
                                                <h6 className="mb-2">View Parts</h6>
                                                <small className="text-muted">Browse all parts</small>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="card h-100 shadow-sm cursor-pointer quick-action-card"
                                             onClick={() => handleNavigateToTab('inspections')}
                                             style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}>
                                            <div className="card-body text-center">
                                                <i className="fas fa-microscope fa-2x text-info mb-3"></i>
                                                <h6 className="mb-2">Inspections</h6>
                                                <small className="text-muted">View raw data</small>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="card h-100 shadow-sm cursor-pointer quick-action-card"
                                             onClick={() => handleNavigateToTab('stations')}
                                             style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}>
                                            <div className="card-body text-center">
                                                <i className="fas fa-server fa-2x text-success mb-3"></i>
                                                <h6 className="mb-2">Stations</h6>
                                                <small className="text-muted">Station analysis</small>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="card h-100 shadow-sm cursor-pointer quick-action-card"
                                             onClick={() => handleNavigateToTab('predictive-insights')}
                                             style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}>
                                            <div className="card-body text-center">
                                                <i className="fas fa-lightbulb fa-2x text-warning mb-3"></i>
                                                <h6 className="mb-2">Insights</h6>
                                                <small className="text-muted">AI predictions</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Recently Used Parts */}
                    <div className="col-lg-8 mb-4">
                        <div className="card shadow-sm h-100">
                            <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <i className="fas fa-history text-primary me-2"></i>
                                    Recently Inspected Parts
                                </h5>
                                <button 
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleNavigateToTab('projects')}
                                >
                                    View All Parts
                                </button>
                            </div>
                            <div className="card-body">
                                {isLoadingProjects ? (
                                    <div className="text-center py-4">
                                        <LoadingSpinner message="Loading recent parts..." size="small" />
                                    </div>
                                ) : recentProjects.length > 0 ? (
                                    <div className="row g-3">
                                        {recentProjects.slice(0, 6).map(project => (
                                            <div key={project.project_name} className="col-md-6 col-lg-4">
                                                <RecentPartCard
                                                    project={project}
                                                    onViewDetails={handleViewPartDetails}
                                                    showConfidence={show_confidence_scores}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-4">
                                        <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                                        <p className="text-muted">No recent parts available</p>
                                        <button 
                                            className="btn btn-primary btn-sm"
                                            onClick={() => handleNavigateToTab('projects')}
                                        >
                                            Browse All Parts
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Defect Rate Trend - Full Width */}
                <div className="row">
                    <div className="col-12 mb-4">
                        <div className="card shadow-sm">
                            <div className="card-header bg-light">
                                <h5 className="mb-0">
                                    <i className="fas fa-chart-line text-primary me-2"></i>
                                    Defect Rate Trend
                                </h5>
                            </div>
                            <div className="card-body">
                                {trendData && trendData.length > 0 ? (
                                    <div style={{ height: '400px', position: 'relative', width: '100%' }}>
                                        <TrendChart
                                            key={`trend-${state.filters.dateRange}-${state.filters.project}-${state.filters.station}-${includeManualImages}`}
                                            data={trendData}
                                            title="Defect Rate Over Time"
                                            height={400}
                                            chartId="overview-trend-chart"
                                        />
                                    </div>
                                ) : (
                                    <div className="text-center p-5">
                                        <i className="fas fa-chart-line fa-4x text-muted mb-4"></i>
                                        <h4 className="text-muted mb-3">No trend data available</h4>
                                        <p className="text-muted">
                                            Trend data will appear once you have sufficient inspection history
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <ProjectDetailsModal
                    project={selectedProject}
                    isOpen={showProjectDetails}
                    onClose={handleCloseProjectDetails}
                />
            </div>
        </ErrorBoundary>
    );
};

// Enhanced Recent Part Card Component with configuration awareness
const RecentPartCard = ({ project, onViewDetails, showConfidence = true }) => {
    const defectRate = project.total_inspections > 0 
        ? ((project.defect_count / project.total_inspections) * 100).toFixed(1) 
        : "0.0";
    
    const defectRateColor = parseFloat(defectRate) > 10 ? 'danger' : 
                           parseFloat(defectRate) > 5 ? 'warning' : 'success';

    const formatTimeAgo = (dateString) => {
        if (!dateString) return 'Never';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    return (
        <div 
            className="card h-100 shadow-sm border-0 recent-part-card"
            style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
            onClick={() => onViewDetails(project)}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="card-title mb-0 text-truncate" style={{ maxWidth: '70%' }}>
                        {project.project_name}
                    </h6>
                    <span className={`badge bg-${defectRateColor}`}>
                        {defectRate}%
                    </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">
                        <i className="fas fa-microscope me-1"></i>
                        {project.total_inspections?.toLocaleString() || 0}
                    </small>
                    <small className="text-muted">
                        <i className="fas fa-clock me-1"></i>
                        {formatTimeAgo(project.last_inspection)}
                    </small>
                </div>
                
                {/* Conditionally show confidence info based on user preferences */}
                {showConfidence && project.confidence && (
                    <div className="mb-2">
                        <small className="text-muted">
                            <i className="fas fa-brain me-1"></i>
                            Confidence: {(project.confidence * 100).toFixed(1)}%
                        </small>
                    </div>
                )}
                
                <div className="progress" style={{ height: '4px' }}>
                    <div 
                        className={`progress-bar bg-${defectRateColor}`}
                        style={{ width: `${Math.min(parseFloat(defectRate), 100)}%` }}
                    ></div>
                </div>
            </div>
        </div>
    );
};